import React, { createContext, useContext, useEffect, useState } from 'react';
import WebSocketService from '../../websockets/WebSocketService';
import { DataContext } from './DataContext';
import {parseBroadcastTime} from "@dashboard/grid-components/announcements/AnnouncementUtil";
import {apiFetch} from "../../../apiClient";

const ExchangeAnnContext = createContext();
export const useAnnouncementContext = () => useContext(ExchangeAnnContext);

// symbol → { bseAnnouncements, nseAnnouncements, bseNew, nseNew, listeners }
const globalSymbolStateMap = new Map();

export const AnnouncementProvider = ({
                                         symbol,
                                         initialBSE = [],
                                         initialNSE = [],
                                         children
                                     }) => {
    const [, forceUpdate] = useState(0);
    const [isUnmounting, setIsUnmounting] = useState(false);

    const {
        setInitialSymbolToBSEAnnouncementsMap,
        setInitialSymbolToNSEAnnouncementsMap
    } = useContext(DataContext);

    // Make sure initial announcements are tagged with exchange
    const taggedInitialBSE = (initialBSE || []).map(a => ({
        ...a,
        exchange: a.exchange || 'BSE'
    }));

    const taggedInitialNSE = (initialNSE || []).map(a => ({
        ...a,
        exchange: a.exchange || 'NSE'
    }));

    const isNse = (a) => ((a?.exchange || '').toString().toUpperCase() === 'NSE');

    const getTimeKey = (a) => {
        const raw = a?.BroadcastTime ?? a?.broadcastTime;
        return parseBroadcastTime(raw); // epoch millis
    };

    const getMsgKey = (a) => {
        if (!a) return '';
        return isNse(a)
            ? (a.Details ?? a.details ?? a.message ?? '').toString().trim()
            : (a.message ?? a.Details ?? a.details ?? '').toString().trim();
    };

// optional but safer (prevents “same time + same details” collisions)
    const getSubjectKey = (a) =>
        (a?.Subject ?? a?.subject ?? a?.announcementType ?? '').toString().trim();

    const isSameAnnKey = (x, y) =>
        getTimeKey(x) === getTimeKey(y) &&
        getMsgKey(x) === getMsgKey(y) &&
        getSubjectKey(x) === getSubjectKey(y);

    // Initialize per-symbol state if absent
    if (!globalSymbolStateMap.has(symbol)) {
        globalSymbolStateMap.set(symbol, {
            bseAnnouncements: [...taggedInitialBSE],
            nseAnnouncements: [...taggedInitialNSE],

            bseNew: taggedInitialBSE.filter(a => a.isNew),
            nseNew: taggedInitialNSE.filter(a => a.isNew),

            listeners: new Set()
        });
    }

    const state = globalSymbolStateMap.get(symbol);

    // -------------------------
    // BSE WEBSOCKET HANDLER
    // -------------------------
    useEffect(() => {
        const handleBSE = (message) => {
            const incoming = message?.data?.announcements || [];

            // tag with exchange = BSE
            incoming.forEach(a => {
                if (!a.exchange) a.exchange = 'BSE';
            });

            state.bseAnnouncements.push(...incoming);
            state.bseNew.push(...incoming.filter(a => a.isNew));

            setInitialSymbolToBSEAnnouncementsMap(prev => {
                const existing = prev[symbol] || [];
                return {
                    ...prev,
                    [symbol]: [...existing, ...incoming]
                };
            });

            for (const cb of state.listeners) cb();
        };

        WebSocketService.subscribe("bseAnnouncement", symbol, handleBSE);
        return () => WebSocketService.unsubscribe("bseAnnouncement", symbol, handleBSE);
    }, [symbol]);

    // -------------------------
    // NSE WEBSOCKET HANDLER
    // -------------------------
    useEffect(() => {
        const handleNSE = (message) => {
            const incoming = message?.data?.announcements || [];

            // tag with exchange = NSE
            incoming.forEach(a => {
                if (!a.exchange) a.exchange = 'NSE';
            });

            state.nseAnnouncements.push(...incoming);
            state.nseNew.push(...incoming.filter(a => a.isNew));

            setInitialSymbolToNSEAnnouncementsMap(prev => {
                const existing = prev[symbol] || [];
                return {
                    ...prev,
                    [symbol]: [...existing, ...incoming]
                };
            });

            for (const cb of state.listeners) cb();
        };

        WebSocketService.subscribe("nseAnnouncement", symbol, handleNSE);
        return () => WebSocketService.unsubscribe("nseAnnouncement", symbol, handleNSE);
    }, [symbol]);

    // Register forceUpdate listener
    useEffect(() => {
        const update = () => forceUpdate(n => n + 1);
        state.listeners.add(update);
        return () => state.listeners.delete(update);
    }, [symbol]);

    const isNseAnnouncement = (a) => (a?.exchange || '').toString().toUpperCase() === 'NSE';

    const getAnnouncementMessageForKey = (a) => {
        if (!a) return '';
        return isNseAnnouncement(a) ? (a.Details ?? a.details ?? '') : (a.message ?? '');
    };

    const markOldOnBackend = async (symbol, anns) => {
        if (!symbol || !anns?.length) return;

        // ✅ remove '-' and anything after it
        const normalizedSymbol = symbol.toString().split('-')[0];

        const toMarkOld = anns.map(a => ({
            broadcastTime: (a.BroadcastTime ?? a.broadcastTime ?? '').toString(),
            message: getAnnouncementMessageForKey(a).toString(),
        }));

        if (!toMarkOld.length) return;

        try {
            await apiFetch('/api/announcements/markOld', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    symbol: normalizedSymbol,
                    toMarkOld
                }),
            });
        } catch (err) {
            console.error('Error marking announcements as old:', err);
        }
    };



    // -------------------------
    // CLEAR MULTIPLE ANNOUNCEMENTS (BSE + NSE AWARE)
    // -------------------------
    const clearAnnouncements = (callbackOrEmpty) => {
        const combinedNew = [...state.bseNew, ...state.nseNew];

        let updatedCombinedNew;
        if (typeof callbackOrEmpty === 'function') {
            updatedCombinedNew = callbackOrEmpty([...combinedNew]);
        } else {
            updatedCombinedNew = [];
        }

        const newBseNew = updatedCombinedNew.filter(a => (a.exchange || '').toUpperCase() === 'BSE');
        const newNseNew = updatedCombinedNew.filter(a => (a.exchange || '').toUpperCase() === 'NSE');

        const clearedBse = state.bseNew.filter(a => !newBseNew.includes(a));
        const clearedNse = state.nseNew.filter(a => !newNseNew.includes(a));

        // ✅ persist on backend
        const cleared = [...clearedBse, ...clearedNse];
        markOldOnBackend(symbol, cleared);

        state.bseNew = newBseNew;
        state.nseNew = newNseNew;

        // Update BSE map: mark cleared ones as isNew=false
        setInitialSymbolToBSEAnnouncementsMap(prevMap => {
            if (!clearedBse.length) return prevMap;

            const next = {};
            for (const [sym, list] of Object.entries(prevMap)) {
                const updated = list.map(a => {
                    const isCleared = clearedBse.some(c => isSameAnnKey(c, a));
                    return isCleared ? { ...a, isNew: false } : a;
                });
                next[sym] = updated;
            }
            return { ...prevMap, ...next };
        });

        // Update NSE map: mark cleared ones as isNew=false
        setInitialSymbolToNSEAnnouncementsMap(prevMap => {
            if (!clearedNse.length) return prevMap;

            const next = {};
            for (const [sym, list] of Object.entries(prevMap)) {
                const updated = list.map(a => {
                    const isCleared = clearedNse.some(c => isSameAnnKey(c, a));
                    return isCleared ? { ...a, isNew: false } : a;
                });
                next[sym] = updated;
            }
            return { ...prevMap, ...next };
        });

        // notify UI
        for (const cb of state.listeners) cb();
    };

    // Derived combined lists for consumers (AnnouncementList)
    const announcements = [...state.bseAnnouncements, ...state.nseAnnouncements];
    const newAnnouncements = [...state.bseNew, ...state.nseNew];

    return (
        <ExchangeAnnContext.Provider
            value={{
                // combined view
                announcements,
                newAnnouncements,
                clearAnnouncements,

                // still expose per-exchange, in case you need them elsewhere
                bseAnnouncements: [...state.bseAnnouncements],
                nseAnnouncements: [...state.nseAnnouncements],
                bseNew: [...state.bseNew],
                nseNew: [...state.nseNew],

                isUnmounting,
                setIsUnmounting
            }}
        >
            {children}
        </ExchangeAnnContext.Provider>
    );
};
